import React, { useState } from 'react';
import { Table, Button, message, Space, Tag, Row, Col, Card, Statistic, Modal, Form, Input, Select, InputNumber, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import dayjs from 'dayjs';

const { Option } = Select;

const OnlineIncome = () => {
    const { t } = useTranslation();
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingIncome, setEditingIncome] = useState(null);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    // Fetch income
    const { data: incomeData, isLoading } = useQuery('online-income', async () => {
        const response = await api.get('/online-income', {
            params: { limit: 10000 }
        });
        return response.data;
    });

    // Fetch online students for dropdown
    const { data: studentsData } = useQuery('online-students', async () => {
        const response = await api.get('/online-students', {
            params: { limit: 10000 }
        });
        return response.data;
    });

    // Create income mutation
    const createIncomeMutation = useMutation(
        async (values) => {
            const response = await api.post('/online-income', values);
            return response.data;
        },
        {
            onSuccess: () => {
                message.success(t('onlineAccounting.incomeCreated'));
                queryClient.invalidateQueries('online-income');
                queryClient.invalidateQueries('online-students');
                setIsModalVisible(false);
                form.resetFields();
                setEditingIncome(null);
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('onlineAccounting.errorCreatingIncome'));
            },
        }
    );

    // Update income mutation
    const updateIncomeMutation = useMutation(
        async ({ id, values }) => {
            const response = await api.put(`/online-income/${id}`, values);
            return response.data;
        },
        {
            onSuccess: () => {
                message.success(t('onlineAccounting.incomeUpdated'));
                queryClient.invalidateQueries('online-income');
                queryClient.invalidateQueries('online-students');
                setIsModalVisible(false);
                form.resetFields();
                setEditingIncome(null);
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('onlineAccounting.errorUpdatingIncome'));
            },
        }
    );

    // Delete income mutation
    const deleteIncomeMutation = useMutation(
        async (id) => {
            const response = await api.delete(`/online-income/${id}`);
            return response.data;
        },
        {
            onSuccess: () => {
                message.success(t('onlineAccounting.incomeDeleted'));
                queryClient.invalidateQueries('online-income');
                queryClient.invalidateQueries('online-students');
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('onlineAccounting.errorDeletingIncome'));
            },
        }
    );

    const handleCreateIncome = async (values) => {
        if (editingIncome) {
            updateIncomeMutation.mutate({ id: editingIncome._id, values });
        } else {
            createIncomeMutation.mutate(values);
        }
    };

    const handleEdit = (record) => {
        setEditingIncome(record);
        form.setFieldsValue({
            student: record.student?._id || record.student,
            amount: record.amount,
            currency: record.currency,
            paymentMethod: record.paymentMethod,
            paymentType: record.paymentType,
            status: record.status,
            transactionId: record.transactionId,
            notes: record.notes,
        });
        setIsModalVisible(true);
    };

    const handleDelete = (id) => {
        deleteIncomeMutation.mutate(id);
    };

    const columns = [
        {
            title: t('onlineAccounting.incomeNumber'),
            dataIndex: 'incomeNumber',
            key: 'incomeNumber',
        },
        {
            title: t('onlineAccounting.date'),
            dataIndex: 'paymentDate',
            key: 'paymentDate',
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
        },
        {
            title: t('onlineAccounting.studentName'),
            dataIndex: 'studentName',
            key: 'studentName',
        },
        {
            title: t('onlineAccounting.course'),
            dataIndex: 'courseName',
            key: 'courseName',
        },
        {
            title: t('onlineAccounting.amount'),
            key: 'amount',
            render: (_, record) => {
                return `${record.currency === 'IQD' ? 'IQD' : '$'}${record.amount?.toLocaleString() || '0'}`;
            },
        },
        {
            title: t('onlineAccounting.paymentMethod'),
            dataIndex: 'paymentMethod',
            key: 'paymentMethod',
            render: (method) => t(`onlineAccounting.${method}`),
        },
        {
            title: t('onlineAccounting.paymentType'),
            dataIndex: 'paymentType',
            key: 'paymentType',
            render: (type) => <Tag>{t(`onlineAccounting.${type}`)}</Tag>,
        },
        {
            title: t('onlineAccounting.status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const color = status === 'completed' ? 'green' : status === 'pending' ? 'orange' : 'red';
                return <Tag color={color}>{t(`onlineAccounting.${status}`)}</Tag>;
            },
        },
        {
            title: t('onlineAccounting.transactionId'),
            dataIndex: 'transactionId',
            key: 'transactionId',
            render: (id) => id || '-',
        },
        {
            title: t('onlineAccounting.actions'),
            key: 'actions',
            fixed: 'right',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title={t('onlineAccounting.confirmDelete')}
                        onConfirm={() => handleDelete(record._id)}
                        okText={t('onlineAccounting.yes')}
                        cancelText={t('onlineAccounting.no')}
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const filteredData = incomeData?.data?.filter((income) => {
        if (searchText) {
            const search = searchText.toLowerCase();
            return (
                income.studentName?.toLowerCase().includes(search) ||
                income.incomeNumber?.toLowerCase().includes(search) ||
                income.courseName?.toLowerCase().includes(search) ||
                income.transactionId?.toLowerCase().includes(search)
            );
        }
        return true;
    });

    // Calculate statistics
    const totalIncomeUSD = incomeData?.totals?.find(t => t._id === 'USD')?.total || 0;
    const totalIncomeIQD = incomeData?.totals?.find(t => t._id === 'IQD')?.total || 0;
    const totalCount = filteredData?.length || 0;

    return (
        <div>
            <h1>{t('onlineAccounting.income')}</h1>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}>
                    <Card>
                        <Statistic title={t('onlineAccounting.totalPayments')} value={totalCount} />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic title={t('onlineAccounting.totalUSD')} value={totalIncomeUSD} prefix="$" precision={2} valueStyle={{ color: '#3f8600' }} />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic title={t('onlineAccounting.totalIQD')} value={totalIncomeIQD} suffix="IQD" precision={0} valueStyle={{ color: '#3f8600' }} />
                    </Card>
                </Col>
            </Row>

            <Space style={{ marginBottom: 16 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setEditingIncome(null);
                        form.resetFields();
                        setIsModalVisible(true);
                    }}
                >
                    {t('onlineAccounting.addIncome')}
                </Button>
                <input
                    placeholder={t('onlineAccounting.searchIncome')}
                    className="ant-input"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 300 }}
                />
            </Space>

            <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="_id"
                loading={isLoading}
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title={editingIncome ? t('onlineAccounting.editIncome') : t('onlineAccounting.addIncome')}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    form.resetFields();
                    setEditingIncome(null);
                }}
                onOk={() => form.submit()}
                confirmLoading={createIncomeMutation.isLoading || updateIncomeMutation.isLoading}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreateIncome}
                >
                    <Form.Item
                        name="student"
                        label={t('onlineAccounting.student')}
                        rules={[{ required: true, message: t('onlineAccounting.selectStudent') }]}
                    >
                        <Select
                            showSearch
                            placeholder={t('onlineAccounting.selectStudent')}
                            disabled={!!editingIncome}
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {studentsData?.data?.map((student) => (
                                <Option key={student._id} value={student._id}>
                                    {student.firstName} {student.lastName} - {student.courseName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="amount"
                        label={t('onlineAccounting.amount')}
                        rules={[{ required: true, message: t('onlineAccounting.enterAmount') }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            placeholder={t('onlineAccounting.enterAmount')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="currency"
                        label={t('onlineAccounting.currency')}
                        initialValue="USD"
                        rules={[{ required: true }]}
                    >
                        <Select>
                            <Option value="USD">USD</Option>
                            <Option value="IQD">IQD</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="paymentMethod"
                        label={t('onlineAccounting.paymentMethod')}
                        initialValue="online"
                        rules={[{ required: true }]}
                    >
                        <Select>
                            <Option value="bank-transfer">{t('onlineAccounting.bank-transfer')}</Option>
                            <Option value="card">{t('onlineAccounting.card')}</Option>
                            <Option value="online">{t('onlineAccounting.online')}</Option>
                            <Option value="paypal">{t('onlineAccounting.paypal')}</Option>
                            <Option value="stripe">{t('onlineAccounting.stripe')}</Option>
                            <Option value="cryptocurrency">{t('onlineAccounting.cryptocurrency')}</Option>
                            <Option value="other">{t('onlineAccounting.other')}</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="paymentType"
                        label={t('onlineAccounting.paymentType')}
                        initialValue="enrollment"
                        rules={[{ required: true }]}
                    >
                        <Select>
                            <Option value="enrollment">{t('onlineAccounting.enrollment')}</Option>
                            <Option value="monthly">{t('onlineAccounting.monthly')}</Option>
                            <Option value="installment">{t('onlineAccounting.installment')}</Option>
                            <Option value="full-payment">{t('onlineAccounting.full-payment')}</Option>
                            <Option value="other">{t('onlineAccounting.other')}</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="status"
                        label={t('onlineAccounting.status')}
                        initialValue="completed"
                        rules={[{ required: true }]}
                    >
                        <Select>
                            <Option value="pending">{t('onlineAccounting.pending')}</Option>
                            <Option value="completed">{t('onlineAccounting.completed')}</Option>
                            <Option value="failed">{t('onlineAccounting.failed')}</Option>
                            <Option value="refunded">{t('onlineAccounting.refunded')}</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="transactionId"
                        label={t('onlineAccounting.transactionId')}
                    >
                        <Input placeholder={t('onlineAccounting.enterTransactionId')} />
                    </Form.Item>

                    <Form.Item
                        name="notes"
                        label={t('onlineAccounting.notes')}
                    >
                        <Input.TextArea rows={3} placeholder={t('onlineAccounting.enterNotes')} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default OnlineIncome;
